import { useLinks } from 'core/components/linksContext';
import { column, container, link, row } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { stringify as stringifyQueryString } from 'query-string';
import React, { useEffect } from 'react';
import { Redirect } from 'react-router';
import { Link, Route, Switch } from 'react-router-dom';
import { useAuth } from 'site/components/auth/AuthProvider';
import AuthWindow from 'site/components/auth/AuthWindow';
import AccountCourses from './Courses/AccountCourses';
import AccountProfile from './Profile/AccountProfile';
import ProfileReferral from './Referral/ProfileReferral';
import SubscriptionsContainer from './Subscriptions/Subscriptions';

const TabLink: React.FC<{ to: string; classes: any; query?: any }> = ({ to, children, classes, query }) => (
    <Route path={to}>
        {({ match }) => (
            <li className={!match ? classes.menu__linkNotActive : classes.menu__linkActive}>
                <Link to={`${to}?${stringifyQueryString(query)}`}>{children}</Link>
            </li>
        )}
    </Route>
);

const TabExit: React.FC<any> = ({ logout, children, classes }) => (
    <li className={classes.menu__linkNotActive}>
        <a onClick={logout}>{children}</a>
    </li>
);

type IAccountMasterProps = {
    isShowcase?: boolean;
} & WithStyles<typeof styles>;

const AccountMaster: React.FC<IAccountMasterProps> = ({ classes, isShowcase = false }) => {
    const { logout, myAccount } = useAuth();
    const links = useLinks();

    useEffect(() => {
        // If we're on an unsupported browser, do nothing
        if (!(window?.OneSignal?.isPushNotificationsSupported && window.OneSignal.isPushNotificationsSupported())) {
            return;
        }

        // Показываем подписку только для авторизированных пользователей
        if (!myAccount) {
            return;
        }

        // Показываем окно с подпиской через 3 секунды после входа в ЛК
        setTimeout(() => {
            // https://documentation.onesignal.com/docs/web-push-sdk#ispushnotificationsenabled
            window.OneSignal.isPushNotificationsEnabled(isEnabled => {
                if (!isEnabled) {
                    // https://documentation.onesignal.com/docs/web-push-sdk#shownativeprompt
                    window.OneSignal.showNativePrompt();
                }
            });
        }, 3000);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <AuthWindow>
            <div>
                <div className={classes.account__top}>
                    <div className={classes.account__container}>
                        <h1 className={classes.title}>Мой кабинет</h1>
                    </div>
                </div>
                <div className={classes.menu}>
                    <div className={classes.account__container}>
                        <div className={classes.menu_links}>
                            <ul className={classes.menu_links__column}>
                                <TabLink to={`${links.account.url()}profile`} classes={classes}>
                                    Мой профиль
                                </TabLink>
                                <TabLink to={`${links.account.url()}courses`} classes={classes}>
                                    Мои курсы
                                </TabLink>
                            </ul>
                            <ul className={classes.menu_links__column}>
                                <TabLink
                                    to={`${links.account.url()}subscriptions`}
                                    classes={classes}
                                    query={{ tab: 'subscriptions' }}
                                >
                                    Мои подписки
                                </TabLink>
                                <TabLink to={`${links.account.url()}referral`} classes={classes}>
                                    Реферальная программа
                                </TabLink>
                                {isShowcase && (
                                    <TabExit logout={logout} classes={classes}>
                                        Выход
                                    </TabExit>
                                )}
                            </ul>
                        </div>
                    </div>
                </div>
                <div className={classes.content}>
                    <div className={classes.account__container}>
                        <Switch>
                            <Route path={`${links.account.url()}profile`} exact component={AccountProfile} />
                            <Route path={`${links.account.url()}courses`} exact component={AccountCourses} />
                            <Route path={`${links.account.url()}subscriptions`} exact>
                                <SubscriptionsContainer isShowcase={isShowcase} />
                            </Route>
                            <Route path={`${links.account.url()}referral`} exact component={ProfileReferral} />
                            <Redirect from={links.account.url()} to={`${links.account.url()}courses`} />
                        </Switch>
                    </div>
                </div>
            </div>
        </AuthWindow>
    );
};

const menuLink = (theme: ITheme) => ({
    display: 'inline-block',
    fontSize: 1.2 + 'rem',
    marginRight: 50,
    padding: '20px 0',
    borderBottom: '5px solid',
    transition: 'border-color 0.25s',
    '& a': {
        ...link(theme.colors.contrastBgText),
        transition: 'color 0.25s',
        '&:hover, &:active, &:focus': {
            color: theme.colors.accent,
            borderBottomColor: theme.colors.accent,
            textDecoration: 'none',
        },
    },

    [theme.breakpoints.down('xs')]: {
        marginRight: 0,
        fontSize: '1rem',
    },
});

const styles = (theme: ITheme) =>
    createStyles({
        account__top: {
            backgroundColor: theme.colors.contrastBg,
            color: theme.colors.contrastBgText,
        },
        account__container: {
            ...container(theme),
        },
        title: {
            marginTop: 0,
            padding: '40px 0',
            marginBottom: 0,
        },
        menu: {
            marginTop: -1,
            [theme.breakpoints.down('sm')]: {
                backgroundColor: theme.colors.contrastBg,
                color: theme.colors.contrastBgText,
            },
        },
        menu_links: {
            ...row(),
            marginTop: 0,
            paddingLeft: 15,
            fontWeight: 700,
            marginBottom: 27,
        },
        menu_links__column: {
            marginBottom: 0,
            paddingLeft: 0,

            [theme.breakpoints.down('xs')]: {
                ...column({ cols: 6 }),
                paddingLeft: 0,
                paddingRight: 0,
            },
        },
        menu__linkNotActive: {
            ...menuLink(theme),
            borderBottomColor: theme.colors.contrastBg,
            [theme.breakpoints.up('md')]: {
                borderBottomColor: 'transparent',
            },
            '& a': {
                ...menuLink(theme)['& a'],
                [theme.breakpoints.up('md')]: {
                    color: 'black',
                },
            },
        },
        menu__linkActive: {
            ...menuLink(theme),
            borderBottomColor: theme.colors.accentMenu,
            '& a': {
                ...menuLink(theme)['& a'],
                color: theme.colors.accentMenu,
            },
            '&:hover, &:active, &:focus': {
                color: theme.colors.accent,
                borderBottomColor: theme.colors.accent,
            },
        },
        content: {},
    });

export default injectSheet(styles, 'AccountMaster')(AccountMaster);
